export const BLOCK_TYPE = {
  TEXT: 'text',
  IMAGE: 'image',
  VIDEO: 'video',
  BUTTON: 'button',
  GROUP: 'group',
  COLLECTION: 'collection',
  BLOG_WIDGET: 'blog_widget',
  CASE_RESULT_WIDGET: 'case_result_widget',
  INDEX_COLLECTION: 'index_collection',
  REVIEW_WIDGET: 'review_widget',
  REVIEW_INDEX: 'review_index',
  EMBED: 'embed',
  RAW_HTML: 'raw_html',
  FORM: 'form',
} as const;

export const BLOCK_TYPES = Object.values(BLOCK_TYPE);

export type BlockType = (typeof BLOCK_TYPE)[keyof typeof BLOCK_TYPE];
export type AddElementBlockType = Extract<
  BlockType,
  'text' | 'image' | 'video' | 'button' | 'embed' | 'form'
>;

export const DATA_WIDGET_BLOCK_TYPES: ReadonlySet<BlockType> = new Set([
  BLOCK_TYPE.BLOG_WIDGET,
  BLOCK_TYPE.CASE_RESULT_WIDGET,
  BLOCK_TYPE.REVIEW_WIDGET,
]);

export const PARENT_BLOCK_TYPES: ReadonlySet<BlockType> = new Set([
  BLOCK_TYPE.GROUP,
  BLOCK_TYPE.COLLECTION,
  BLOCK_TYPE.INDEX_COLLECTION,
  ...DATA_WIDGET_BLOCK_TYPES,
]);

export function isParentBlockType(blockType: BlockType): boolean {
  return PARENT_BLOCK_TYPES.has(blockType);
}

export function isVisualMediaBlockType(blockType: BlockType): boolean {
  return (
    blockType === BLOCK_TYPE.IMAGE ||
    blockType === BLOCK_TYPE.VIDEO ||
    blockType === BLOCK_TYPE.EMBED
  );
}

export interface PageBlock {
  id: string;
  /**
   * Stable client-only key used for editor identity.
   * Keeps drag/selection/render state stable when autosave remaps temp block IDs.
   */
  uiKey?: string;
  /** Cross-version public identity; public DOM `data-block-id`. Not `id` or `uiKey`. */
  stableId?: string;
  blockType: BlockType;
  props: Record<string, unknown>;
  parentBlockId?: string | null;
  columnSpan: number;
  position: number;
  visible: boolean;
}

/** A bounded subtree whose descendants may be structurally edited. */
export interface BlockStructuralRegion {
  rootBlockId: string;
  allowedBlockTypes: readonly AddElementBlockType[];
  protectedBlockIds: readonly string[];
}

/** Editor render / DnD key (`uiKey ?? id`). Not the persisted cross-version `stableId`. */
export const getBlockUiKey = (block: Pick<PageBlock, 'id' | 'uiKey'>): string =>
  block.uiKey ?? block.id;
